import { watch } from 'vue'
import {
  deviceManagementAcceptorsService,
  DMAcceptorsError,
  useWorkingTimeStore,
  AcceptorsHooksManager,
  type HookErrorResponse
} from '@nsftx/seven-client-retail-vue-dm'
import { logService } from '@nsftx/seven-client-retail-utils'
import i18n from '@/plugins/translationsPlugin'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import {
  closeDMAcceptorsErrorNotifications,
  showDMAcceptorsErrorNotification
} from '@/modules/acceptors/acceptorsService'
import errorParser from '@/utils/errorParser'

const LOG_PREFIX = '[workingTimeService]'

let workingTimeValidationTimeout: number | undefined
let deregisterBeforeAcceptorsStartHook: () => void

const attachHooks = () => {
  deregisterBeforeAcceptorsStartHook?.()

  deregisterBeforeAcceptorsStartHook = AcceptorsHooksManager.hook(
    'BeforeAcceptorsStart',
    (params) => {
      const workingTimeStore = useWorkingTimeStore()
      const { isValid } = workingTimeStore.isWorkingTimeValid()

      if (!isValid) {
        params.errorResponses.push({
          id: 'acceptors-start-error-working-time',
          message: 'Acceptors could not be started. Working time is over.',
          code: 'ST_BEFORE_ACCEPTORS_START_WORKING_TIME_ERROR'
        })
      }
    }
  )
}

const stopAcceptors = () => {
  deviceManagementAcceptorsService.stopAcceptors().catch((err) => {
    logService.error(`${LOG_PREFIX} Acceptors could not be stopped.`, {
      code: 'ST_WORKING_TIME_ACC_STOP_ERR',
      ...errorParser.parseUpstream(err)
    })
  })
}

const startAcceptors = () => {
  closeDMAcceptorsErrorNotifications()
  deviceManagementAcceptorsService
    .startAcceptors()
    .catch((error: Array<HookErrorResponse> | DMAcceptorsError) => {
      logService.error(`${LOG_PREFIX} Acceptors could not be started.`, {
        code: 'ST_WORKING_TIME_START_ACCEPTORS_ERROR',
        details: error,
        ...errorParser.parseUpstream(error)
      })

      showDMAcceptorsErrorNotification(error)
    })
}

const startWorkingTimeValidation = () => {
  const workingTimeStore = useWorkingTimeStore()
  const notificationsStore = useNotificationsStore()
  const { t } = i18n.global
  const { isValid, nextValidTime, scheduleTime } = workingTimeStore.isWorkingTimeValid()

  notificationsStore.closeNotificationById('working_time_error')

  if (!isValid) {
    const time = nextValidTime || t('common_unknown')
    notificationsStore.showNotification({
      id: 'working_time_error',
      text: [t('working_time_over', { time })],
      variant: 'warning',
      timeout: null
    })
  }

  if (isValid) {
    startAcceptors()
  } else {
    stopAcceptors()
  }

  if (workingTimeValidationTimeout) {
    clearTimeout(workingTimeValidationTimeout)
  }

  workingTimeValidationTimeout = window.setTimeout(startWorkingTimeValidation, scheduleTime)
}

const initWorkingTimeService = () => {
  const workingTimeStore = useWorkingTimeStore()
  attachHooks()

  /**
   * If working time is changed during the time app is started, we need to restart
   * validation and use new data.
   */
  watch(() => workingTimeStore.workingTimeData, startWorkingTimeValidation, { immediate: false })

  startWorkingTimeValidation()
}

export { initWorkingTimeService }
